import { QR_READING_DIALOG_SOURCE } from '@/shared/lib/qr-resolver/dialogSource'
import type { QrResolution } from '@/shared/lib/qr-resolver/resolveQrCode'
import { alert } from '@/shared/ui/dialog'

import type { CheckInFromQrResolutionResult } from './checkInFromQrResolution'

// Afirma estado, não evento: `done` cobre tanto o registro novo quanto a
// releitura de um QR já lido, e o payload do QR não distingue os dois. "Check-in
// realizado com sucesso" seria falso no segundo caso.
const partnerName = (resolution: QrResolution): string | undefined => {
  const name =
    resolution.status === 'resolved' ? resolution.resource.name : undefined

  return typeof name === 'string' && name.length > 0 ? name : undefined
}

// Não é "Check-in feito": esse texto já é do perfil — `Show.jsx` o usa no badge
// e `PartnerCheckInMarker` no `aria-label` do botão. Repeti-lo no título do
// diálogo dá dois alvos para o mesmo nome acessível, o que quebra o e2e em
// strict mode e faz o leitor de tela anunciar duas coisas diferentes com a
// mesma etiqueta.
const ALREADY_DONE_TITLE = 'Você já fez check-in aqui'

// Diálogo, não toast. O toast empilhava no canto e competia com o conteúdo da
// própria tela de destino; o participante acabava de fazer uma ação deliberada
// e merece uma confirmação que ele fecha, no meio da tela.
//
// `DialogHost` mora no `AppShell` (`app/AppShell.tsx:116`), então o diálogo
// sobrevive à navegação que acontece logo antes — e é por isso que a
// confirmação é emitida **depois** de navegar, já sobre o perfil do parceiro.
// `alreadyDone` vem do progresso que o app já tem em cache
// (`useUserProgress().partnerWasFinished`), lido **antes** do registro — o
// payload do QR não carrega `finished`, e uma consulta a mais só para escolher
// a frase não se paga.
//
// O cache pode estar velho (revalida a cada 60s). Por isso a afirmação só é
// feita na direção segura: dizemos "já estava feito" apenas quando o cache
// afirma que estava. Cache vazio ou velho cai na confirmação neutra, que é
// verdadeira nos dois casos.
export const notifyPartnerCheckIn = (
  result: CheckInFromQrResolutionResult,
  resolution: QrResolution,
  alreadyDone = false
): void => {
  if (result === 'skipped') return

  const name = partnerName(resolution)
  const onde = name ? ` em ${name}` : ''

  // `alreadyDone` afirma o ESTADO, e o estado não depende do desfecho desta
  // tentativa. Numa releitura em que o registro falhou — rede instável no
  // estande — alarmar seria dizer que o check-in não existe quando ele existe:
  // exatamente o que `requirements.md § Releitura não duplica nem alarma`
  // proíbe. Por isso este ramo vem antes de `failed`.
  if (alreadyDone) {
    void alert({
      variant: 'success',
      title: ALREADY_DONE_TITLE,
      description: `Você já tinha feito check-in${onde}. Não precisa de novo.`,
      confirmLabel: 'Entendi',
      source: QR_READING_DIALOG_SOURCE
    })
    return
  }

  if (result === 'failed') {
    void alert({
      variant: 'destructive',
      title: 'Não foi possível registrar seu check-in',
      // Não promete o botão do perfil. Ele existe numa falha transitória
      // (`Show.jsx`, gate `has_rewards || checkin_active || finished`), mas não
      // existe quando o Parceiro foi desativado depois de o QR ser impresso —
      // e esse é justamente um dos caminhos que chegam aqui.
      description: `Sua presença${onde} não foi registrada. Tente ler o código de novo em instantes; se não der, procure a equipe do parceiro.`,
      confirmLabel: 'Entendi',
      source: QR_READING_DIALOG_SOURCE
    })
    return
  }

  void alert({
    variant: 'success',
    title: 'Check-in confirmado',
    description: `Sua presença${onde} está registrada.`,
    confirmLabel: 'Entendi',
    source: QR_READING_DIALOG_SOURCE
  })
}
